import { type IntegrationServiceId } from '../../../../common/integrations'
import { type UserIntegrations } from '../../../../models/Scheduling'
import { IntegrationConnectionStatus, resolveCalendarOptions } from '../../../Scheduling/onboarding/onboardingIntegrations'

export enum AvailabilityCalendarState {
  CONNECTED = 'connected',
  NO_CALENDAR = 'no_calendar',
  NOT_SELECTED = 'not_selected',
  ISSUE = 'issue',
}

export interface UnhealthyCalendar {
  serviceId: IntegrationServiceId
  label: string
}

export type PrimaryAvailabilityCalendarState =
  | { state: AvailabilityCalendarState.CONNECTED }
  | { state: AvailabilityCalendarState.NO_CALENDAR }
  | { state: AvailabilityCalendarState.NOT_SELECTED }
  | { state: AvailabilityCalendarState.ISSUE; serviceId: IntegrationServiceId }

export interface AvailabilityCalendarStatus {
  primary: PrimaryAvailabilityCalendarState
  /**
   * Connected calendars other than the scheduling one that cannot sync. Only populated
   * when the scheduling calendar is healthy: the warning tells the user clients could
   * book over events Scheduling can no longer see, which only happens once bookings work.
   */
  unhealthyOtherCalendars: UnhealthyCalendar[]
}

export const resolveAvailabilityCalendarStatus = (
  integrations: UserIntegrations,
  isIntegrationAllowed: (serviceId: IntegrationServiceId) => boolean,
): AvailabilityCalendarStatus => {
  const connected = resolveCalendarOptions(integrations, isIntegrationAllowed).filter((option) => option.status !== IntegrationConnectionStatus.NOT_CONNECTED)

  if (connected.length === 0) {
    return { primary: { state: AvailabilityCalendarState.NO_CALENDAR }, unhealthyOtherCalendars: [] }
  }

  const selected = connected.find((option) => option.provider === integrations.selectedCalendar)
  if (!selected) {
    return { primary: { state: AvailabilityCalendarState.NOT_SELECTED }, unhealthyOtherCalendars: [] }
  }
  if (selected.status === IntegrationConnectionStatus.NEEDS_RECONNECT) {
    return { primary: { state: AvailabilityCalendarState.ISSUE, serviceId: selected.serviceId }, unhealthyOtherCalendars: [] }
  }

  return {
    primary: { state: AvailabilityCalendarState.CONNECTED },
    unhealthyOtherCalendars: connected
      .filter((option) => option.provider !== selected.provider && option.status !== IntegrationConnectionStatus.READY)
      .map(({ serviceId, label }) => ({ serviceId, label })),
  }
}
